<template>
  <div class="relative bg-white rounded-2xl shadow-md p-6 mx-auto space-y-2">
    <div class="flex justify-end">
      <StoreConfigurationStatusBadge :configuration-status="store.generalInformationsConfigStatus" />
    </div>
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2">
      <StackedField label="Raison sociale">
        <FormField
          v-if="editMode"
          id="businessName"
          name="businessName"
          v-model="formData.businessName"
          :placeholder="store.businessName"
        />
        <template v-else>{{ store.businessName || "—" }}</template>
      </StackedField>
      <StackedField label="Nom commercial">
        <FormField
          v-if="editMode"
          id="salesChannel"
          name="salesChannel"
          v-model="formData.salesChannel"
          :placeholder="store.salesChannel"
        />
        <template v-else>{{ store.salesChannel || "—" }}</template>
      </StackedField>
      <StackedField label="Adresse postale">
        <LocationField
          v-if="editMode"
          id="shippingAddress"
          placeholder="Adresse postale"
          v-model="formData.shippingAddress"
          required
        />
        <template v-else>{{ formatLocation(store.shippingAddress) || "—" }}</template>
      </StackedField>
      <StackedField label="Adresse de facturation">
        <FormField
          v-if="editMode"
          id="billingAddress"
          name="billingAddress"
          v-model="formData.billingAddress"
          :placeholder="store.billingAddress"
        />
        <template v-else>{{ store.billingAddress || "—" }}</template>
      </StackedField>
      <StackedField label="CA du magasin">
        <Selector
          v-if="editMode"
          id="revenueRange"
          name="revenueRange"
          v-model="formData.revenueRange"
          :options="revenueRangeOptions"
        />
        <template v-else>{{ revenueRangeLabelValue || "—" }}</template>
      </StackedField>
      <StackedField label="Type d’exploitation">
        <Selector
          v-if="editMode"
          id="ownershipType"
          name="ownershipType"
          v-model="formData.ownershipType"
          :options="ownershipTypeOptions"
        />
        <template v-else>{{ ownershipTypeLabelValue || "—" }}</template>
      </StackedField>
    </div>

    <div class="flex flex-row-reverse gap-x-4">
      <template v-if="editMode">
        <SecondaryButton @click="editMode = false">Annuler</SecondaryButton>
        <SecondaryButton @click="submitEdit">Mettre à jour</SecondaryButton>
      </template>
      <template v-else>
        <SecondaryButton v-if="validationNeeded" @click="validate">Valider</SecondaryButton>
        <SecondaryButton v-if="validationNeeded" @click="reject">Refuser</SecondaryButton>
        <SecondaryButton v-if="!editMode" @click="editMode = true">Modifier</SecondaryButton>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { StackedField, SecondaryButton, Selector, FormField, formatLocation, LocationField } from "@estaly/ui";
import StoreConfigurationStatusBadge from "@/components/StoreConfigurationStatusBadge.vue";

export default {
  components: {
    Selector,
    FormField,
    LocationField,
    StoreConfigurationStatusBadge,
    SecondaryButton,
    StackedField,
  },
  props: ["store", "isStoreSetup"],

  data: () => {
    return {
      editMode: false,
      formData: {
        businessName: "",
        salesChannel: "",
        billingAddress: "",
        shippingAddress: "",
        revenueRange: "",
        ownershipType: "",
      },
    };
  },

  mounted() {
    const {
      generalInformationsConfigStatus,
      businessName,
      salesChannel,
      billingAddress,
      shippingAddress,
      revenueRange,
      ownershipType,
    } = this.store;

    this.formData = {
      generalInformationsConfigStatus,
      businessName,
      salesChannel,
      billingAddress,
      shippingAddress,
      revenueRange,
      ownershipType,
    };
  },

  computed: {
    ...mapGetters("stores", ["getRevenueRanges", "getOwnershipTypes", "isPendingValidation"]),

    generalInformations() {
      return this.store.configurationGeneralInformations;
    },

    revenueRangeLabelValue() {
      return this.store.revenueRange ? this.$t(`organization.revenue_range.${this.store.revenueRange}`) : null;
    },

    ownershipTypeLabelValue() {
      return this.store.ownershipType ? this.$t(`store.ownership_type.${this.store.ownershipType}`) : null;
    },

    revenueRangeOptions() {
      return this.getRevenueRanges().map((revenueRange) => ({
        value: revenueRange,
        label: this.$t(`organization.revenue_range.${revenueRange}`),
      }));
    },

    ownershipTypeOptions() {
      return this.getOwnershipTypes().map((ownershipType) => ({
        value: ownershipType,
        label: this.$t(`store.ownership_type.${ownershipType}`),
      }));
    },

    pendingValidation() {
      return this.isPendingValidation(this.store, "generalInformationsConfigStatus");
    },

    validationNeeded() {
      return this.isStoreSetup && this.pendingValidation;
    },
  },

  methods: {
    formatLocation,
    ...mapActions("notifications", ["notify"]),
    ...mapActions("stores", ["updateGeneralInformations", "update"]),

    validate() {
      this.$emit("validate");
    },

    reject() {
      this.$emit("reject");
    },

    async submitEdit() {
      const response = this.isStoreSetup
        ? await this.updateGeneralInformations({ storeId: this.store.id, ...this.formData })
        : await this.update({ storeId: this.store.id, data: this.formData });

      if (!response?.success) return;

      this.notify({
        category: "simple",
        type: "success",
        title: "Mise à jour effectuée",
      });
      this.$emit("updated");
      this.editMode = false;
    },
  },
};
</script>
